import { useState } from 'react';
import { Box, Button, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { importVenues, uploadVenueFile } from '../../../../services/venueManagementService';
import { UPLOAD_FILE_PATTERN, UPLOAD_TYPES } from './venueManagementConstants';
import { BulkUploadResultModal } from './BulkUploadResultModal';

const ADDED_LABELS = [['countries', 'countries'], ['states', 'states'], ['cities', 'cities'], ['venues', 'venues']];

const resultHeadline = (result) => {
    const added = ADDED_LABELS
        .filter(([key]) => result.added?.[key])
        .map(([key, label]) => `${result.added[key]} ${label}`);
    let headline = added.length ? `Added: ${added.join(', ')}` : 'Nothing new added';
    if (result.updated) headline += ` · ${result.updated} venues updated`;
    if (result.skipped) headline += ` · ${result.skipped} already existed`;
    return headline;
};

const IMPORT_SOURCE_LABEL = 'Exam venue details';

// Two tabs: Bulk upload (one upload box for every file type; a type stays disabled until its parent
// records exist) and Import venue (pulls venues from the exam venue details service).
export const BulkUploadCard = ({ uploadType, onUploadTypeChange, locations, onUploaded }) => {

    const [activeTab, setActiveTab] = useState('upload');
    const [isImporting, setIsImporting] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [fileName, setFileName] = useState('');
    const [isResultOpen, setIsResultOpen] = useState(false);

    const typeInfo = UPLOAD_TYPES.find((item) => item.key === uploadType) ?? UPLOAD_TYPES[0];
    const isTypeAvailable = (item) => !item.requires || locations[item.requires].length > 0;

    const handleFile = async (file) => {
        if (!file || isUploading) return;
        if (!UPLOAD_FILE_PATTERN.test(file.name)) {
            setResult({ fatal: 'Use an .xlsx, .xls or .csv file.' });
            return;
        }
        setIsUploading(true);
        setResult(null);
        setFileName(file.name);
        try {
            const res = await uploadVenueFile(typeInfo.key, file);
            if (res.code === 'OK') {
                setResult(res.data);
                setIsResultOpen(true);
                onUploaded();
            }
        } catch (error) {
            setResult({ fatal: error?.response?.data?.message ?? 'The file could not be uploaded.' });
            console.log('There is an error while uploading venue file', error);
        } finally {
            setIsUploading(false);
        }
    }

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
    }

    const onImport = async () => {
        if (isImporting) return;
        setIsImporting(true);
        setResult(null);
        setFileName(IMPORT_SOURCE_LABEL);
        try {
            const res = await importVenues();
            if (res.code === 'OK') {
                setResult(res.data);
                setIsResultOpen(true);
                onUploaded();
            }
        } catch (error) {
            setResult({ fatal: error?.response?.data?.message ?? 'Venues could not be imported.' });
            console.log('There is an error while importing venues', error);
        } finally {
            setIsImporting(false);
        }
    }

    const onTabChange = (e, tab) => {
        setActiveTab(tab);
        setResult(null);
    }

    return (
        <Box className='venue-card'>
            <Tabs value={activeTab} onChange={onTabChange} variant='fullWidth' className='bulk-tabs'>
                <Tab value='upload' label='Bulk upload' disabled={isImporting} />
                <Tab value='import' label='Import venue' disabled={isUploading} />
            </Tabs>

            {activeTab === 'import' ? (
                <Box className='import-zone'>
                    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                        <path d='M12 3v12m0 0-4-4m4 4 4-4' /><path d='M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4' />
                    </svg>
                    <Typography variant='body2' color='text.secondary'>
                        Import venues from the exam venue details service. Existing codes are updated.
                    </Typography>
                    <LoadingButton variant='contained' color='secondary' loading={isImporting} onClick={onImport}
                        sx={{ borderRadius: 40, textTransform: 'uppercase', fontWeight: 500 }}>
                        Import
                    </LoadingButton>
                </Box>
            ) : (<>
            <TextField select size='small' fullWidth label='File type' value={typeInfo.key}
                onChange={(e) => { onUploadTypeChange(e.target.value); setResult(null); }}>
                {UPLOAD_TYPES.map((item) => (
                    <MenuItem key={item.key} value={item.key} disabled={!isTypeAvailable(item)}>{item.label}</MenuItem>
                ))}
            </TextField>

            <Box className={`drop-zone${isDragOver ? ' drop-zone--over' : ''}`}
                onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                onDrop={onDrop}>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M12 15V3m0 0-4 4m4-4 4 4' /><path d='M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4' />
                </svg>
                <Typography variant='body2' color='text.secondary'>Drop .xlsx / .xls / .csv here</Typography>
                <LoadingButton component='label' variant='contained' color='secondary' loading={isUploading} sx={{ borderRadius: 40, textTransform: 'none' }}>
                    Select File
                    <input hidden type='file' accept='.xlsx,.xls,.csv'
                        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
                </LoadingButton>
            </Box>
            </>)}

            {result && (
                result.fatal ? (
                    <Box className='upload-result upload-result--bad'>
                        <b>Not uploaded</b>
                        <span>{result.fatal}</span>
                    </Box>
                ) : (
                    <Box className={`upload-result ${result.errors?.length ? 'upload-result--bad' : 'upload-result--good'}`}>
                        <b>{resultHeadline(result)}</b>
                        <span>Saved {result.succeeded?.length ?? 0} rows · Failed {result.errors?.length ?? 0} rows</span>
                        <Button size='small' sx={{ alignSelf: 'flex-start', p: 0, textTransform: 'none' }} onClick={() => setIsResultOpen(true)}>
                            View details
                        </Button>
                    </Box>
                )
            )}

            <BulkUploadResultModal open={isResultOpen} result={result} fileName={fileName} onClose={() => setIsResultOpen(false)} />
        </Box>
    );
}
